import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Crown, Star, Check, X, Gear, CaretRight, ArrowUp, User } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import SubscriptionNotice from "./SubscriptionNotice";
import { isNative } from "../lib/native";
import { manageNativeSubscriptions } from "../lib/iap";

const GREEN = "#17E37E";
const GOLD = "#D9B36A";

// Sober per-tier presentation (no shine, no criard colour).
const TIER = {
  free: { label: "Profil", color: "rgba(255,255,255,0.7)", border: "rgba(255,255,255,0.16)", bg: "rgba(255,255,255,0.05)", icon: User },
  starter: { label: "Starter", color: "rgba(255,255,255,0.85)", border: "rgba(255,255,255,0.18)", bg: "rgba(255,255,255,0.06)", icon: Check },
  pro: { label: "Pro", color: GREEN, border: "rgba(23,227,126,0.4)", bg: "rgba(23,227,126,0.08)", icon: Star },
  vip: { label: "VIP", color: GOLD, border: "rgba(217,179,106,0.45)", bg: "rgba(217,179,106,0.1)", icon: Crown },
};

const PREVIEW_PLANS = [
  { id: "starter", name: "Starter", price: "6,99", tagline: "1 sport · prédictions IA de base", color: "rgba(255,255,255,0.9)" },
  { id: "pro", name: "Pro", price: "19,99", tagline: "3 sports · analyses illimitées + scores probables", color: GREEN, recommended: true },
  { id: "vip", name: "VIP", price: "29,99", tagline: "Tout Pro + analyses de nos experts", color: GOLD },
];

const LOCALE = { fr: "fr-FR", en: "en-GB", es: "es-ES" };

// ---------------------------------------------------------------- shared modal
function PlansModal({ currentPlan, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return createPortal(
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4 py-6 overflow-y-auto"
      data-testid="plans-preview-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl p-5 fade-up"
        style={{ background: "#0B0D0C", border: "1px solid rgba(255,255,255,0.1)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-xl font-black">{t("statusbadge.modal_title")}</h3>
          <button data-testid="plans-preview-close" onClick={onClose} className="text-white/40 hover:text-white">
            <X size={22} weight="bold" />
          </button>
        </div>
        <p className="text-[12px] text-white/45 mb-4">{t("statusbadge.modal_sub")}</p>

        <div className="space-y-2.5">
          {PREVIEW_PLANS.map((p) => {
            const isCurrent = p.id === currentPlan;
            return (
              <div
                key={p.id}
                data-testid={`plan-preview-${p.id}`}
                className="relative rounded-2xl px-4 py-3.5 flex items-center gap-3"
                style={{
                  background: isCurrent ? "rgba(255,255,255,0.06)" : p.recommended ? "rgba(23,227,126,0.07)" : "rgba(255,255,255,0.03)",
                  border: `1px solid ${isCurrent ? p.color : p.recommended ? "rgba(23,227,126,0.5)" : "rgba(255,255,255,0.1)"}`,
                  boxShadow: isCurrent ? `0 0 0 1px ${p.color} inset` : "none",
                }}
              >
                {isCurrent ? (
                  <span data-testid={`plan-current-${p.id}`} className="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest"
                    style={{ background: p.color, color: "#04120A" }}>
                    {t("statusbadge.current")}
                  </span>
                ) : p.recommended && (
                  <span className="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest"
                    style={{ background: GREEN, color: "#04120A" }}>
                    {t("statusbadge.recommended")}
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg font-black leading-none" style={{ color: p.color }}>{p.name}</p>
                  <p className="text-[11px] text-white/45 mt-1 leading-snug">{p.tagline}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className="font-numeral font-black text-xl">{p.price}€</span>
                  <span className="block text-[10px] text-white/40">/mois</span>
                </div>
              </div>
            );
          })}
        </div>

        <button
          data-testid="plans-preview-cta"
          onClick={() => { onClose(); navigate("/paywall"); }}
          className="w-full mt-5 h-12 rounded-xl font-bold text-sm text-black transition-transform hover:scale-[1.02]"
          style={{ background: GREEN }}
        >
          {currentPlan ? t("statusbadge.cta_change") : t("statusbadge.cta")}
        </button>
      </div>
    </div>,
    document.body
  );
}

// Trigger button: pill (top header) or nav (bottom-nav tab).
function BadgeTrigger({ variant, active, onClick, icon: Icon, label, color, bg, border, iconWeight = "fill" }) {
  if (variant === "nav") {
    return (
      <button data-testid="status-badge" onClick={onClick}
        className="w-full flex flex-col items-center gap-1 py-3 relative">
        {active && <span className="absolute top-0 w-10 h-0.5 rounded-full bg-gold glow-gold" />}
        <Icon size={24} weight={iconWeight} color={color} />
        <span data-testid="status-badge-label" className="text-[10px] font-semibold" style={{ color }}>{label}</span>
      </button>
    );
  }
  return (
    <button data-testid="status-badge" onClick={onClick}
      className="px-3 py-1.5 rounded-full text-[13px] font-bold flex items-center gap-1.5 whitespace-nowrap transition-colors"
      style={{ color, background: bg, border: `1px solid ${border}` }}>
      <Icon size={13} weight={iconWeight} />
      <span data-testid="status-badge-label">{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------- subscriber
function SubscriberBadge({ tier, variant, isPaid = true }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { subscription: sub, refreshUser } = useAuth();
  const [showPlans, setShowPlans] = useState(false);
  const T = TIER[tier];
  const Icon = T.icon;
  // 5ᵉ onglet : icône "personnage" pour tous les plans (couleur selon le plan).
  const NavIcon = variant === "nav" ? User : Icon;
  const navLabel = tier === "free" ? t("statusbadge.profile_tab") : T.label;

  const toggle = () => {
    if (!open) refreshUser().catch(() => {});
    setOpen(!open);
  };

  const manage = async () => {
    setLoading(true);
    try {
      if (isNative()) {
        await manageNativeSubscriptions(sub?.provider);
        setLoading(false);
        return;
      }
      navigate("/paywall");
    } catch {
      setLoading(false);
      setOpen(false);
      navigate("/profile");
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(LOCALE[i18n.language?.slice(0, 2)] || "fr-FR", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return null; }
  };
  const selected = sub?.subscriptions?.find((s) => s.provider === sub.provider);
  const date = sub?.cancel_at_period_end ? selected?.grace_expires_at || selected?.expires_at : sub?.renews_at;
  const renewText = date
    ? t(sub.cancel_at_period_end ? "statusbadge.ends_on" : "statusbadge.renews_on", { date: fmtDate(date) })
    : null;

  const active = open || loc.pathname.startsWith("/profile");
  const popCls = variant === "nav"
    ? "absolute bottom-full mb-2 right-1 w-64 rounded-2xl p-4 z-50 hm-card fade-up"
    : "absolute right-0 mt-2 w-64 rounded-2xl p-4 z-50 hm-card fade-up";

  return (
    <div className={variant === "nav" ? "relative" : "relative shrink-0"}>
      <BadgeTrigger variant={variant} active={active} onClick={toggle}
        icon={NavIcon} label={navLabel} color={T.color} bg={T.bg} border={T.border} />

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="sub-manage-popover" className={popCls} style={{ background: "#101010", border: "1px solid rgba(255,255,255,0.12)" }}>
            <SubscriptionNotice />
            {!isPaid ? (
              <>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">{t("statusbadge.your_plan")}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <User size={18} weight="fill" color={T.color} />
                  <span data-testid="sub-plan-name" className="font-display text-xl font-black" style={{ color: T.color }}>{t("statusbadge.free_plan")}</span>
                </div>
                <p className="text-[11px] text-white/55 mt-1.5">{t("statusbadge.free_desc")}</p>
                <button data-testid="sub-profile-btn" onClick={() => { setOpen(false); navigate("/profile"); }}
                  className="w-full mt-3 h-10 rounded-xl flex items-center justify-center gap-2 text-sm font-bold text-black"
                  style={{ background: T.color }}>
                  <User size={15} weight="fill" /> {t("statusbadge.see_profile")}
                </button>
                <button data-testid="sub-see-offers-btn" onClick={() => { setOpen(false); setShowPlans(true); }}
                  className="w-full mt-2 h-9 rounded-xl text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors"
                  style={{ border: `1px solid ${GREEN}`, color: GREEN }}>
                  <ArrowUp size={13} weight="bold" /> {t("statusbadge.see_offers")}
                </button>
              </>
            ) : (
              <>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">{t("statusbadge.your_plan")}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <Icon size={18} weight="fill" color={T.color} />
                  <span data-testid="sub-plan-name" className="font-display text-xl font-black" style={{ color: T.color }}>{T.label}</span>
                </div>

                <p data-testid="sub-renewal" className="text-[11px] text-white/55 mt-1.5">
                  {sub === null ? t("statusbadge.loading") : (renewText || t("statusbadge.active"))}
                </p>

                {tier === "starter" && (
                  <div data-testid="starter-upsell" className="mt-3 rounded-xl px-3 py-2.5" style={{ background: "rgba(23,227,126,0.07)", border: "1px solid rgba(23,227,126,0.35)" }}>
                    <p className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1" style={{ color: GREEN }}>
                      <ArrowUp size={11} weight="bold" /> {t("statusbadge.pro_adds_title")}
                    </p>
                    <ul className="mt-1.5 space-y-1">
                      {["pro_add_1", "pro_add_2", "pro_add_3"].map((k) => (
                        <li key={k} className="text-[11px] text-white/70 flex items-start gap-1.5">
                          <Check size={12} weight="bold" color={GREEN} className="mt-0.5 shrink-0" /> {t(`statusbadge.${k}`)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button data-testid="sub-manage-btn" onClick={manage} disabled={loading}
                  className="w-full mt-3 h-10 rounded-xl flex items-center justify-center gap-2 text-sm font-bold text-black disabled:opacity-60"
                  style={{ background: T.color }}>
                  <Gear size={15} weight="fill" /> {loading ? t("statusbadge.opening") : t("statusbadge.manage")}
                </button>
                <button data-testid="sub-compare-btn" onClick={() => { setOpen(false); setShowPlans(true); }}
                  className="w-full mt-2 h-9 rounded-xl text-[12px] font-semibold text-white/60 hover:text-white transition-colors"
                  style={{ border: "1px solid rgba(255,255,255,0.12)" }}>
                  {t("statusbadge.compare")}
                </button>
                <button data-testid="sub-profile-btn" onClick={() => { setOpen(false); navigate("/profile"); }}
                  className="w-full mt-2 h-8 rounded-xl flex items-center justify-center gap-1 text-[12px] font-semibold text-white/45 hover:text-white/75 transition-colors">
                  {t("statusbadge.see_profile")} <CaretRight size={13} weight="bold" />
                </button>
              </>
            )}
          </div>
        </>
      )}

      {showPlans && <PlansModal currentPlan={isPaid ? tier : null} onClose={() => setShowPlans(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------- guest
function GuestBadge({ variant }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const muted = "rgba(255,255,255,0.55)";
  return (
    <div className={variant === "nav" ? "relative" : "contents"}>
      <BadgeTrigger variant={variant} active={open} onClick={() => setOpen(true)}
        icon={Crown} iconWeight="regular" label={t("statusbadge.plans")}
        color={variant === "nav" ? muted : "rgba(255,255,255,0.7)"}
        bg="rgba(255,255,255,0.05)" border="rgba(255,255,255,0.14)" />
      {open && <PlansModal currentPlan={null} onClose={() => setOpen(false)} />}
    </div>
  );
}

export const StatusBadge = ({ variant = "pill" }) => {
  const { user } = useAuth();
  if (!user) return <GuestBadge variant={variant} />; // non connecté → uniquement les offres
  const plan = user.plan;
  const isPaid = ["starter", "pro", "vip"].includes(plan) || user.role === "admin";
  const tier = ["starter", "pro", "vip"].includes(plan) ? plan : isPaid ? "pro" : "free"; // admin sans plan payant → Pro ; inscrit gratuit → Profil
  return <SubscriberBadge tier={tier} variant={variant} isPaid={isPaid} />;
};
